import { truthyPredicate } from '@iconik-io/core/src/truthy_predicate';

import type { DownloadRequestBody } from '../agent/agent_api_client';
import type { SegmentEventOptions } from './segment/segment_tracking';

const DOWNLOAD_EVENT_NAME = 'Download';

export type DownloadHandler = 'Agent' | 'Browser';

export function uniqueFormats(
  names: readonly (string | undefined)[],
): string[] | undefined {
  const formats = Array.from(new Set(names.filter(truthyPredicate))).sort();
  return formats.length > 0 ? formats : undefined;
}

/**
 * What a download request asked the Agent for. Reading it back off the request
 * body keeps the tracked values from drifting from what was actually requested.
 */
export function describeDownloadRequest(body: DownloadRequestBody): {
  formats?: string[];
  itemCount: number;
  objectTypes: string[];
} {
  const assetLike = [
    ...(body.assets ?? []),
    ...(body.assetVersions ?? []),
    ...(body.fileSets ?? []),
    ...(body.files ?? []),
  ];
  const collections = body.collections ?? [];
  const playlists = body.playlists ?? [];

  const objectTypes: string[] = [];
  if (assetLike.length > 0) {
    objectTypes.push('assets');
  }
  if (collections.length > 0) {
    objectTypes.push('collections');
  }
  if (playlists.length > 0) {
    objectTypes.push('playlists');
  }

  return {
    // File set and file requests carry no format
    formats: uniqueFormats(
      [
        ...(body.assets ?? []),
        ...(body.assetVersions ?? []),
        ...collections,
        ...playlists,
      ].map(({ format }) => format),
    ),
    itemCount: assetLike.length + collections.length + playlists.length,
    objectTypes,
  };
}

export type DownloadEventArgs = {
  formats?: readonly string[];
  itemCount: number;
  objectTypes?: readonly string[];
  handledBy: DownloadHandler;
  isShare: boolean;
};

export function makeDownloadEvent(
  args: DownloadEventArgs,
): SegmentEventOptions {
  const { formats, itemCount, objectTypes, handledBy, isShare } = args;
  return {
    eventName: DOWNLOAD_EVENT_NAME,
    properties: {
      Formats: formats,
      'Item Count': itemCount,
      'Object Types': objectTypes,
      'Handled By': handledBy,
      'Is Share': isShare,
    },
  };
}
